/**
 * The App Shell stylesheet.
 *
 * A single string rather than a `.css` file so the shell stays one lazy-free import
 * with no extra network request on a Low_Bandwidth_Connection (Requirement 14.2).
 * It is small: a toolbar row, a formula bar row, the grid pane with two drawn
 * scrollbars, a status bar, and a tab strip.
 *
 * `.sr-only` is the standard clip-rect pattern rather than `display: none`: content
 * inside it stays in the accessibility tree (and in the DOM for the integration
 * suite) while taking no visual space.
 */

export const SHELL_STYLE_ID = 'spreadsheet-shell-style';

export const SHELL_CSS = `
/* ═══════════════════════════════════════════════════════════════════════════
   Design Tokens — NumixSheet Data Command Center
   Deep Indigo (#090d16, #0f172a), Electric Violet (#6366f1), Cyan (#06b6d4), Warm Amber
   ═══════════════════════════════════════════════════════════════════════════ */
:root {
  /* Surface colors */
  --ws-bg-body: #090d16;
  --ws-bg-surface: #ffffff;
  --ws-bg-surface-alt: #f8fafc;
  --ws-bg-surface-hover: #f1f5f9;
  --ws-bg-surface-active: #e2e8f0;
  
  /* Next-Gen Command Center Header & Rail */
  --ws-bg-header: #0f172a;
  --ws-bg-rail: #090d16;
  --ws-bg-rail-hover: rgba(255, 255, 255, 0.08);
  --ws-bg-rail-active: rgba(99, 102, 241, 0.18);
  --ws-rail-border: #1e293b;
  --ws-contextual-bar-bg: #f8fafc;

  --ws-bg-toolbar: #f8fafc;
  --ws-bg-formula-bar: #ffffff;
  --ws-bg-tab-bar: #0f172a;
  --ws-bg-modal: #ffffff;
  --ws-bg-modal-backdrop: rgba(15, 23, 42, 0.75);
  --ws-bg-input: #ffffff;
  --ws-bg-input-alt: #f8fafc;
  --ws-bg-code: #0f172a;

  /* Text colors */
  --ws-text-primary: #0f172a;
  --ws-text-secondary: #475569;
  --ws-text-tertiary: #64748b;
  --ws-text-muted: #94a3b8;
  --ws-text-inverse: #f8fafc;
  --ws-text-header: #ffffff;
  --ws-text-header-muted: #94a3b8;
  --ws-text-link: #6366f1;

  /* Border colors */
  --ws-border: #e2e8f0;
  --ws-border-strong: #cbd5e1;
  --ws-border-subtle: #f1f5f9;
  --ws-border-header: #1e293b;
  --ws-border-input: #cbd5e1;
  --ws-border-input-focus: #6366f1;

  /* Brand / accent (Electric Violet & Cyan) */
  --ws-accent: #6366f1;
  --ws-accent-hover: #4f46e5;
  --ws-accent-light: #e0e7ff;
  --ws-accent-bg: rgba(99, 102, 241, 0.12);
  --ws-accent-gradient: linear-gradient(135deg, #6366f1 0%, #06b6d4 100%);
  --ws-accent-shadow: rgba(99, 102, 241, 0.35);

  /* Semantic colors */
  --ws-success: #10b981;
  --ws-warning: #f59e0b;
  --ws-error: #ef4444;
  --ws-error-bg: #fef2f2;
  --ws-error-border: #fecaca;
  --ws-info: #06b6d4;

  /* Shadows */
  --ws-shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.04);
  --ws-shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);
  --ws-shadow-lg: 0 12px 30px -5px rgba(0, 0, 0, 0.15);
  --ws-shadow-header: 0 1px 3px rgba(0, 0, 0, 0.2);

  /* Grid-specific */
  --ws-grid-line: #e2e8f0;
  --ws-grid-header-bg: #f8fafc;
  --ws-grid-header-text: #334155;
  --ws-grid-selection: rgba(99, 102, 241, 0.14);
  --ws-grid-selection-border: #6366f1;
  --ws-grid-freeze-line: #6366f1;

  /* Scrollbar */
  --ws-scrollbar-track: #f1f5f9;
  --ws-scrollbar-thumb: #cbd5e1;
  --ws-scrollbar-thumb-hover: #94a3b8;

  /* Typography */
  --ws-font-sans: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  --ws-font-mono: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  --ws-font-size-xs: 11px;
  --ws-font-size-sm: 12px;
  --ws-font-size-base: 13px;
  --ws-font-size-md: 14px;
  --ws-font-size-lg: 16px;
  --ws-font-size-xl: 18px;

  /* Spacing */
  --ws-radius-sm: 4px;
  --ws-radius-md: 6px;
  --ws-radius-lg: 8px;
  --ws-radius-xl: 12px;
  --ws-radius-pill: 20px;

  /* Transitions */
  --ws-transition: 0.15s ease;
}

/* ═══════════════════════════════════════════════════════════════════════════
   Global Reset & Full Height Application Shell
   ═══════════════════════════════════════════════════════════════════════════ */
html, body {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

body {
  font: var(--ws-font-size-base) var(--ws-font-sans);
  color: var(--ws-text-primary);
  background: var(--ws-bg-body);
  transition: background var(--ws-transition), color var(--ws-transition);
}

#app {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* ═══════════════════════════════════════════════════════════════════════════
   App Shell Two-Column Framework (.ws-shell)
   Left Rail + Right Main Workspace
   ═══════════════════════════════════════════════════════════════════════════ */
.ws-shell {
  display: flex;
  flex-direction: row;
  height: 100vh;
  height: 100dvh;
  width: 100vw;
  overflow: hidden;
  background: #090d16;
  position: relative;
}

/* ═══════════════════════════════════════════════════════════════════════════
   1. Left Navigation Rail (.rz-nav-rail)
   ═══════════════════════════════════════════════════════════════════════════ */
.rz-nav-rail {
  width: 220px;
  min-width: 220px;
  background: #090d16;
  border-right: 1px solid #1e293b;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 10px;
  box-sizing: border-box;
  user-select: none;
  z-index: 50;
  transition: width 0.2s cubic-bezier(0.16, 1, 0.3, 1), min-width 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.rz-nav-rail.is-collapsed {
  width: 60px;
  min-width: 60px;
  padding: 12px 6px;
}

.rz-rail-top {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.rz-rail-brand {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 6px 12px 6px;
  border-bottom: 1px solid #1e293b;
}

.rz-rail-logo {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}

.rz-rail-icon {
  width: 32px;
  height: 32px;
  min-width: 32px;
  background: linear-gradient(135deg, #6366f1 0%, #06b6d4 100%);
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  font-size: 14px;
  color: #ffffff;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
  letter-spacing: -0.5px;
}

.rz-rail-title {
  font-weight: 800;
  font-size: 16px;
  color: #f8fafc;
  letter-spacing: -0.4px;
  white-space: nowrap;
}

.rz-nav-rail.is-collapsed .rz-rail-title {
  display: none;
}

.rz-rail-collapse-btn {
  background: transparent;
  border: 1px solid #334155;
  color: #94a3b8;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  transition: all 0.15s ease;
}

.rz-rail-collapse-btn:hover {
  background: #1e293b;
  color: #f8fafc;
  border-color: #6366f1;
}

.rz-rail-nav-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rz-rail-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 9px 12px;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #94a3b8;
  font-size: 13px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
  width: 100%;
  box-sizing: border-box;
}

.rz-rail-item:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #f8fafc;
}

.rz-rail-item.is-active {
  background: rgba(99, 102, 241, 0.16);
  color: #818cf8;
  border-left: 3px solid #6366f1;
}

.rz-rail-item-icon {
  font-size: 16px;
  min-width: 20px;
  text-align: center;
}

.rz-rail-item-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rz-nav-rail.is-collapsed .rz-rail-item {
  justify-content: center;
  padding: 10px 0;
}

.rz-nav-rail.is-collapsed .rz-rail-item-label {
  display: none;
}

.rz-rail-bottom {
  padding-top: 10px;
  border-top: 1px solid #1e293b;
}

/* ═══════════════════════════════════════════════════════════════════════════
   2. Main Workspace Layout (.rz-main-workspace)
   ═══════════════════════════════════════════════════════════════════════════ */
.rz-main-workspace {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  overflow: hidden;
  position: relative;
}

/* ═══════════════════════════════════════════════════════════════════════════
   3. Top Command Center Header (.rz-top-header)
   ═══════════════════════════════════════════════════════════════════════════ */
.rz-top-header {
  height: 48px;
  min-height: 48px;
  background: #0f172a;
  border-bottom: 1px solid #1e293b;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  box-sizing: border-box;
  user-select: none;
  gap: 16px;
  z-index: 40;
}

.rz-header-workspace-select {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid #334155;
  color: #f1f5f9;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
}

.rz-header-workspace-select:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: #6366f1;
}

.rz-header-command-trigger {
  flex: 1;
  max-width: 520px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid #334155;
  border-radius: 8px;
  padding: 6px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #94a3b8;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rz-header-command-trigger:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: #6366f1;
  color: #f8fafc;
}

.rz-trigger-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rz-trigger-kbd {
  background: #1e293b;
  border: 1px solid #475569;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  color: #cbd5e1;
  font-family: var(--ws-font-mono);
}

.rz-header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rz-save-indicator {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #10b981;
  font-weight: 500;
  margin-right: 4px;
}

.rz-save-dot {
  width: 7px;
  height: 7px;
  background: #10b981;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.6);
}

.rz-header-action-btn {
  background: transparent;
  border: none;
  color: #94a3b8;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  transition: all 0.15s ease;
}

.rz-header-action-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
}

.rz-share-primary-btn {
  background: linear-gradient(135deg, #6366f1 0%, #06b6d4 100%);
  color: #ffffff;
  border: none;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35);
  transition: all 0.15s ease;
}

.rz-share-primary-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.5);
}

.rz-user-profile-badge {
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
  color: #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 11px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

/* ═══════════════════════════════════════════════════════════════════════════
   4. Dataset Meta Info Banner (.rz-dataset-banner)
   ═══════════════════════════════════════════════════════════════════════════ */
.rz-dataset-banner {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  padding: 6px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 12px;
  user-select: none;
}

.rz-dataset-meta-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ws-doc-title {
  background: transparent;
  border: 1px solid transparent;
  color: #0f172a;
  font-weight: 700;
  font-size: 13px;
  padding: 3px 8px;
  border-radius: 6px;
  outline: none;
  font-family: inherit;
  width: 160px;
  transition: all 0.15s ease;
}

.ws-doc-title:hover, .ws-doc-title:focus {
  background: #ffffff;
  border-color: #cbd5e1;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.rz-dataset-badge {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.rz-perf-badge {
  background: #f0fdf4;
  border-color: #bbf7d0;
  color: #166534;
}

/* ═══════════════════════════════════════════════════════════════════════════
   5. Dynamic Contextual Action Bar (.rz-contextual-bar)
   ═══════════════════════════════════════════════════════════════════════════ */
.rz-contextual-bar {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 4px 16px;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  user-select: none;
  min-height: 38px;
  box-sizing: border-box;
}

.rz-contextual-actions-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.rz-ctx-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #334155;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.rz-ctx-btn:hover {
  background: #f1f5f9;
  border-color: #cbd5e1;
  color: #0f172a;
}

.rz-ctx-btn.is-primary {
  background: rgba(99, 102, 241, 0.1);
  border-color: #6366f1;
  color: #4f46e5;
  font-weight: 700;
}

.rz-ctx-btn.is-primary:hover {
  background: #6366f1;
  color: #ffffff;
}

.rz-ctx-btn.is-danger {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

.rz-ctx-btn.is-danger:hover {
  background: #dc2626;
  color: #ffffff;
}

/* ═══════════════════════════════════════════════════════════════════════════
   6. Formula Bar (.formula-bar)
   ═══════════════════════════════════════════════════════════════════════════ */
.formula-bar {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 4px 16px;
  border-bottom: 1px solid #e2e8f0;
  background: #ffffff;
  box-sizing: border-box;
  min-height: 38px;
}

.name-box {
  width: 90px;
  font-family: var(--ws-font-sans);
  font-weight: 700;
  font-size: 12px;
  color: #0f172a;
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  outline: none;
  background: #f8fafc;
  box-sizing: border-box;
  transition: all 0.15s ease;
}

.name-box:focus {
  border-color: #6366f1;
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
}

.formula-fx {
  color: #6366f1;
  font-style: italic;
  font-weight: 800;
  font-size: 14px;
  user-select: none;
  padding: 0 2px;
}

.formula-input {
  flex: 1;
  font-family: var(--ws-font-mono);
  font-size: 13px;
  color: #0f172a;
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  resize: none;
  box-sizing: border-box;
  outline: none;
  height: 28px;
  line-height: 20px;
  background: #ffffff;
  transition: all 0.15s ease;
}

.formula-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
}

.formula-bar.is-expanded .formula-input {
  height: 80px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-all;
}

.formula-expand-btn {
  background: transparent;
  border: none;
  color: #64748b;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
}

.formula-expand-btn:hover {
  background: #f1f5f9;
  color: #0f172a;
}

/* ═══════════════════════════════════════════════════════════════════════════
   7. Spreadsheet Canvas Grid (.grid-view)
   Full flex height occupying all remaining workspace space
   ═══════════════════════════════════════════════════════════════════════════ */
.grid-view {
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  overflow: hidden;
  background: #ffffff;
}

.grid-canvas {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
}

.grid-surface {
  position: absolute;
  inset: 0;
  touch-action: none;
  cursor: cell;
}

.scroll-track {
  position: absolute;
  background: #f8fafc;
  z-index: 10;
}

.scroll-v {
  top: 0;
  right: 0;
  bottom: 12px;
  width: 12px;
  border-left: 1px solid #e2e8f0;
}

.scroll-h {
  left: 0;
  right: 12px;
  bottom: 0;
  height: 12px;
  border-top: 1px solid #e2e8f0;
}

.scroll-thumb {
  position: absolute;
  background: #cbd5e1;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.scroll-thumb:hover {
  background: #94a3b8;
}

.scroll-v .scroll-thumb {
  left: 2px;
  right: 2px;
  top: 0;
}

.scroll-h .scroll-thumb {
  top: 2px;
  bottom: 2px;
  left: 0;
}

/* In-Cell Editor */
.cell-editor {
  position: absolute;
  z-index: 30;
  box-sizing: border-box;
}

.cell-editor[hidden] {
  display: none;
}

.cell-editor-input {
  font: var(--ws-font-size-base) var(--ws-font-sans);
  border: 2px solid #6366f1;
  outline: none;
  background: #ffffff;
  color: #0f172a;
  width: 100%;
  height: 100%;
  padding: 2px 4px;
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
  border-radius: 2px;
}

/* ═══════════════════════════════════════════════════════════════════════════
   8. Sheet Tabs & Status Bar (.tab-bar, .status-bar)
   ═══════════════════════════════════════════════════════════════════════════ */
.tab-bar {
  height: 38px;
  min-height: 38px;
  background: #0f172a;
  border-top: 1px solid #1e293b;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  box-sizing: border-box;
  user-select: none;
}

.tab-nav {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
}

.tab {
  background: transparent;
  border: 1px solid transparent;
  color: #94a3b8;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}

.tab:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #f8fafc;
}

.tab.active {
  background: #1e293b;
  border-color: #334155;
  color: #ffffff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

.tab-add-btn {
  background: transparent;
  border: 1px dashed #334155;
  color: #94a3b8;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  transition: all 0.15s ease;
}

.tab-add-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  border-color: #6366f1;
}

.status-bar {
  display: flex;
  align-items: center;
  gap: 14px;
  font-size: 11px;
  color: #94a3b8;
}

/* ═══════════════════════════════════════════════════════════════════════════
   9. Command Palette Modal (.rz-cmd-backdrop, .rz-cmd-modal)
   ═══════════════════════════════════════════════════════════════════════════ */
.rz-cmd-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(4px);
  z-index: 10000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 80px;
}

.rz-cmd-modal {
  width: 600px;
  max-width: 90vw;
  background: #0f172a;
  border: 1px solid #334155;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: rzCmdIn 0.18s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes rzCmdIn {
  0% { transform: scale(0.96) translateY(-10px); opacity: 0; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
}

.rz-cmd-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid #1e293b;
  background: #090d16;
}

.rz-cmd-icon {
  font-size: 18px;
  color: #6366f1;
}

.rz-cmd-input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  font-size: 15px;
  color: #ffffff;
  font-family: inherit;
}

.rz-cmd-input::placeholder {
  color: #64748b;
}

.rz-cmd-badge {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 10px;
  color: #94a3b8;
  font-weight: 600;
}

.rz-cmd-list {
  max-height: 360px;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.rz-cmd-category-header {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: #64748b;
  padding: 8px 10px 4px 10px;
  letter-spacing: 0.05em;
}

.rz-cmd-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;
  color: #cbd5e1;
  cursor: pointer;
  transition: all 0.12s ease;
}

.rz-cmd-item:hover, .rz-cmd-item.is-active {
  background: rgba(99, 102, 241, 0.18);
  color: #ffffff;
}

.rz-cmd-item-icon {
  font-size: 16px;
}

.rz-cmd-item-info {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.rz-cmd-item-title {
  font-weight: 600;
  font-size: 13px;
}

.rz-cmd-item-sub {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 1px;
}

.rz-cmd-item-kbd {
  background: #1e293b;
  border: 1px solid #334155;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 11px;
  color: #cbd5e1;
  font-family: var(--ws-font-mono);
}

.rz-cmd-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  background: #090d16;
  border-top: 1px solid #1e293b;
  font-size: 11px;
  color: #64748b;
}

.rz-cmd-hints {
  display: flex;
  gap: 12px;
}

/* ═══════════════════════════════════════════════════════════════════════════
   10. Format Drawer (.rz-drawer-overlay, .rz-format-drawer)
   ═══════════════════════════════════════════════════════════════════════════ */
.rz-drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(2px);
  z-index: 10000;
  display: flex;
  justify-content: flex-end;
}

.rz-format-drawer {
  width: 360px;
  height: 100%;
  background: #ffffff;
  box-shadow: -8px 0 25px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  animation: rzDrawerSlide 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  overflow-y: auto;
}

@keyframes rzDrawerSlide {
  0% { transform: translateX(100%); }
  100% { transform: translateX(0); }
}

.rz-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.rz-drawer-title-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rz-drawer-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.rz-drawer-close {
  background: transparent;
  border: none;
  font-size: 16px;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
}

.rz-drawer-close:hover {
  color: #0f172a;
}

.rz-drawer-content {
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.rz-drawer-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rz-section-title {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.rz-format-cards-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.rz-format-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  text-align: left;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rz-format-card:hover {
  background: rgba(99, 102, 241, 0.08);
  border-color: #6366f1;
}

.rz-format-card-sample {
  font-weight: 700;
  font-size: 13px;
  color: #6366f1;
  margin-bottom: 2px;
}

.rz-format-card-label {
  font-weight: 600;
  font-size: 12px;
  color: #0f172a;
}

.rz-format-card-desc {
  font-size: 10px;
  color: #64748b;
}

.rz-format-actions-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.rz-action-button {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rz-action-button:hover {
  background: #e2e8f0;
  color: #0f172a;
}

.rz-format-colors-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.rz-color-preset-card {
  border: 1px solid transparent;
  border-radius: 6px;
  padding: 8px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rz-color-preset-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
}

.rz-color-preset-sample {
  font-weight: 800;
  font-size: 13px;
}

.rz-color-preset-name {
  font-size: 10px;
  margin-top: 2px;
  font-weight: 600;
}

/* ═══════════════════════════════════════════════════════════════════════════
   11. Add Data Modal (.rz-modal-backdrop, .rz-add-data-modal)
   ═══════════════════════════════════════════════════════════════════════════ */
.rz-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.75);
  backdrop-filter: blur(4px);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
}

.rz-add-data-modal {
  width: 720px;
  max-width: 95vw;
  background: #ffffff;
  border-radius: 14px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: rzModalIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes rzModalIn {
  0% { transform: scale(0.95); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

.rz-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.rz-modal-title-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rz-modal-icon {
  width: 40px;
  height: 40px;
  background: rgba(99, 102, 241, 0.12);
  color: #6366f1;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.rz-modal-title {
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}

.rz-modal-desc {
  margin: 2px 0 0 0;
  font-size: 13px;
  color: #64748b;
}

.rz-modal-close {
  background: transparent;
  border: none;
  font-size: 18px;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
}

.rz-modal-close:hover {
  color: #0f172a;
}

.rz-modal-body {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-height: 80vh;
  overflow-y: auto;
}

.rz-data-dropzone {
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 30px 20px;
  text-align: center;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rz-data-dropzone.is-dragover, .rz-data-dropzone:hover {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.04);
}

.rz-drop-icon {
  font-size: 32px;
}

.rz-drop-title {
  font-weight: 700;
  font-size: 15px;
  color: #0f172a;
}

.rz-drop-sub {
  font-size: 12px;
  color: #64748b;
}

.rz-drop-browse-btn {
  margin-top: 6px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rz-drop-browse-btn:hover {
  border-color: #6366f1;
  color: #6366f1;
}

.rz-modal-divider-text {
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #94a3b8;
  position: relative;
}

.rz-data-source-cards {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.rz-source-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rz-source-card:hover {
  border-color: #6366f1;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  transform: translateY(-1px);
}

.rz-source-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.rz-source-icon {
  font-size: 20px;
}

.rz-source-badge {
  background: #f1f5f9;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 10px;
  font-weight: 700;
  color: #475569;
}

.rz-source-title {
  font-weight: 700;
  font-size: 13px;
  color: #0f172a;
  margin-top: 4px;
}

.rz-source-desc {
  font-size: 11px;
  color: #64748b;
  line-height: 1.4;
}

/* ═══════════════════════════════════════════════════════════════════════════
   12. Mobile Bottom Quick Action Bar (.rz-mobile-action-bar)
   ═══════════════════════════════════════════════════════════════════════════ */
.rz-mobile-action-bar {
  display: none;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  padding: 6px 10px;
  justify-content: space-around;
  align-items: center;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.08);
  z-index: 100;
  min-height: 48px;
}

.rz-mobile-action-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #475569;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  min-width: 44px;
  min-height: 44px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.rz-mobile-action-btn:active {
  background: rgba(99, 102, 241, 0.12);
  color: #6366f1;
}

.rz-mobile-action-btn .rz-action-icon {
  font-size: 16px;
  margin-bottom: 2px;
}

/* ═══════════════════════════════════════════════════════════════════════════
   13. Responsive Breakpoints (Desktop > Tablet > Mobile)
   ═══════════════════════════════════════════════════════════════════════════ */
@media (max-width: 1024px) {
  .rz-nav-rail {
    width: 60px;
    min-width: 60px;
    padding: 12px 6px;
  }
  .rz-rail-title, .rz-rail-item-label {
    display: none;
  }
  .rz-rail-item {
    justify-content: center;
    padding: 10px 0;
  }
  .rz-header-command-trigger {
    max-width: 320px;
  }
}

@media (max-width: 768px) {
  .rz-nav-rail {
    display: none;
  }
  .rz-top-header {
    height: 44px;
    min-height: 44px;
    padding: 0 10px;
  }
  .rz-header-workspace-select {
    display: none;
  }
  .rz-dataset-banner {
    padding: 4px 10px;
  }
  .rz-mobile-action-bar {
    display: flex;
  }
}

@media (max-width: 480px) {
  .rz-dataset-meta-group:last-child {
    display: none;
  }
  .formula-bar.is-bottom-sheet {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: #ffffff;
    border-top: 2px solid #6366f1;
    padding: 12px;
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2);
    z-index: 10010;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
}

/* Minimal Canvas + Floating Command Dock — final visual direction. */
.ws-shell {
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: #f7f8fc;
}
.ws-shell > .rz-nav-rail { display: none; }
.ws-shell > .rz-main-workspace {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  position: relative;
}
.rz-main-workspace > .rz-dataset-banner {
  display: block;
  position: absolute;
  top: 17px;
  left: 24px;
  z-index: 5;
  padding: 0;
  background: transparent;
}
.rz-dataset-banner .rz-dataset-meta-group { display: none; }
.rz-dataset-banner .rz-dataset-meta-group:first-child { display: block; }
.rz-dataset-banner .rz-dataset-badge { display: none; }
.rz-dataset-banner .ws-doc-title {
  display: block;
  width: 190px;
  padding: 4px 8px;
  color: #18213b;
  font-size: 15px;
  font-weight: 700;
}
.rz-top-header {
  flex: 0 0 62px;
  height: 62px;
  padding: 0 24px;
  background: rgba(255,255,255,.97);
  color: #18213b;
  border-bottom: 1px solid #e6e8f0;
  box-shadow: 0 4px 18px rgba(29,37,70,.06);
}
.rz-header-workspace-select { display: none; }
.rz-header-command-trigger {
  max-width: 560px;
  height: 38px;
  border: 1px solid #dfe3ee;
  border-radius: 12px;
  background: #f8f9fc;
  color: #7a8195;
}
.rz-header-command-trigger:hover { border-color: #8b5cf6; background: #fff; }
.rz-share-primary-btn { border-radius: 10px; background: #5b21b6; }
.rz-main-workspace > .rz-contextual-bar {
  position: fixed;
  left: 50%;
  bottom: 64px;
  z-index: 30;
  transform: translateX(-50%);
  width: max-content;
  max-width: calc(100vw - 32px);
  padding: 8px 10px;
  border: 1px solid #ddd6fe;
  border-radius: 16px;
  background: rgba(255,255,255,.94);
  box-shadow: 0 12px 34px rgba(45,35,104,.18);
  backdrop-filter: blur(14px);
}

/* Unified productivity suite shell: clear workspace identity and mode switching. */
.rz-top-header { height: 72px; flex-basis: 72px; padding: 0 28px; gap: 24px; background: #111827; border-bottom: 1px solid #263149; box-shadow: 0 8px 24px rgba(15,23,42,.16); }
.rz-header-workspace-select { display: inline-flex; align-items: center; gap: 10px; min-width: 208px; height: 44px; padding: 0 14px; border: 1px solid #384765; border-radius: 12px; background: #1b2539; color: #f8fafc; font-weight: 700; cursor: pointer; }
.rz-header-workspace-select:hover { border-color: #7c8cff; background: #202d46; }
.rz-header-workspace-select > span { white-space: nowrap; }
.rz-header-command-trigger { height: 42px; max-width: 620px; border-color: #384765; background: #1b2539; color: #cbd5e1; }
.rz-header-command-trigger:hover { border-color: #7c8cff; background: #202d46; }
.rz-save-indicator { color: #a7f3d0; }
.rz-presence-status { padding: 7px 10px; border: 1px solid #38506a; border-radius: 999px; background: #172338; color: #a7f3d0; font-size: 11px; font-weight: 800; cursor: pointer; }
.rz-presence-status:hover { border-color: #86efac; background: #20314b; }
.rz-comments-status { padding: 7px 10px; border: 1px solid #38506a; border-radius: 999px; background: #172338; color: #c7d2fe; font-size: 11px; font-weight: 800; cursor: pointer; }
.rz-comments-status:hover { border-color: #a5b4fc; background: #20314b; }
.rz-comments-panel { position: fixed; top: 82px; right: 24px; z-index: 80; display: flex; flex-direction: column; width: min(360px, calc(100vw - 32px)); max-height: min(620px, calc(100vh - 110px)); padding: 16px; border: 1px solid #dbe2ea; border-radius: 16px; background: #fff; box-shadow: 0 20px 50px rgba(15,23,42,.2); }
.rz-comments-panel[hidden] { display: none; }
.rz-comments-heading { display: flex; align-items: center; justify-content: space-between; padding-bottom: 12px; border-bottom: 1px solid #e5e7eb; color: #172033; }
.rz-comments-close { border: 0; background: transparent; color: #64748b; font-size: 20px; cursor: pointer; }
.rz-comments-list { flex: 1; overflow-y: auto; padding: 10px 0; }
.rz-comments-empty { color: #94a3b8; font-size: 13px; text-align: center; }
.rz-comment-item { margin: 0 0 10px; padding: 10px; border-radius: 10px; background: #f8fafc; }
.rz-comment-item strong { color: #334155; font-size: 12px; }
.rz-comment-item p { margin: 5px 0 0; color: #475569; font-size: 13px; line-height: 1.4; }
.rz-comment-input { width: 100%; box-sizing: border-box; resize: vertical; margin-top: 8px; padding: 9px; border: 1px solid #cbd5e1; border-radius: 9px; font: inherit; }
.rz-comment-submit { margin-top: 8px; padding: 9px 12px; border: 0; border-radius: 9px; background: #5b5cf0; color: #fff; font-weight: 700; cursor: pointer; }
.rz-header-action-btn { color: #cbd5e1; }
.rz-main-workspace > .rz-dataset-banner { top: 16px; left: 28px; }
.rz-main-workspace > .rz-contextual-bar { position: static; order: 3; min-height: 58px; padding: 10px 28px; background: #ffffff; border-bottom: 1px solid #e5e7eb; box-shadow: none; }
.rz-contextual-actions-group { width: 100%; max-width: none; justify-content: flex-start; }
.rz-ctx-btn { height: 36px; border-radius: 9px; }
.rz-ctx-btn.is-primary { background: #5b5cf0; border-color: #5b5cf0; }
.rz-main-workspace > .formula-bar { order: 4; }
.rz-main-workspace > .grid-view { order: 5; }
.rz-main-workspace > .tab-bar { order: 6; }
.nx-mode-backdrop[hidden] { display: none; }
.nx-mode-backdrop { position: fixed; inset: 0; z-index: 100; display: grid; place-items: center; padding: 24px; background: rgba(15,23,42,.58); backdrop-filter: blur(8px); }
.nx-mode-dialog { position: relative; width: min(760px, 100%); padding: 34px; border: 1px solid #e5e7eb; border-radius: 24px; background: #fff; box-shadow: 0 30px 90px rgba(15,23,42,.28); }
.nx-mode-heading { max-width: 560px; }
.nx-mode-kicker { color: #5b5cf0; font-size: 11px; font-weight: 800; letter-spacing: .16em; }
.nx-mode-heading h2 { margin: 8px 0 6px; color: #111827; font-size: 28px; }
.nx-mode-heading p { margin: 0 0 26px; color: #64748b; }
.nx-mode-close { position: absolute; top: 18px; right: 18px; width: 34px; height: 34px; border: 0; border-radius: 9px; background: #f1f5f9; color: #475569; font-size: 22px; cursor: pointer; }
.nx-mode-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
.nx-mode-card { display: flex; flex-direction: column; align-items: flex-start; gap: 14px; min-height: 190px; padding: 20px; border: 1px solid #e2e8f0; border-radius: 16px; background: #f8fafc; color: #1e293b; text-align: left; cursor: pointer; transition: transform .18s ease, border-color .18s ease, box-shadow .18s ease; }
.nx-mode-card:hover, .nx-mode-card.is-current { transform: translateY(-3px); border-color: #7c8cff; box-shadow: 0 12px 26px rgba(91,92,240,.14); }
.nx-mode-icon { display: grid; place-items: center; width: 40px; height: 40px; border-radius: 12px; background: #e0e7ff; color: #4f46e5; font-size: 22px; }
.nx-mode-card strong { display: block; margin-bottom: 6px; font-size: 16px; }
.nx-mode-card small { display: block; color: #64748b; font-size: 12px; line-height: 1.45; }
.nx-mode-arrow { margin-top: auto; color: #5b5cf0; font-size: 18px; }
.nx-editor-mode[hidden] { display: none; }
.nx-editor-mode { position: fixed; inset: 0; z-index: 90; display: flex; flex-direction: column; background: #eef2f7; color: #172033; }
.nx-editor-header { display: flex; align-items: center; gap: 18px; min-height: 70px; padding: 12px 24px; background: #111827; color: #fff; border-bottom: 1px solid #27334b; }
.nx-editor-close { border: 1px solid #43516e; border-radius: 9px; padding: 9px 12px; background: #1d2940; color: #f8fafc; cursor: pointer; }
.nx-editor-mode-label { min-width: 110px; color: #a5b4fc; font-size: 13px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.nx-editor-title { flex: 1; max-width: 470px; padding: 10px 12px; border: 1px solid #43516e; border-radius: 9px; background: #1d2940; color: #fff; font-size: 15px; }
.nx-editor-saved { margin-left: auto; color: #a7f3d0; font-size: 12px; }
.nx-editor-tools { display: flex; gap: 8px; padding: 12px 24px; background: #fff; border-bottom: 1px solid #dbe2ea; }
.nx-editor-conflict { position: absolute; top: 76px; right: 24px; z-index: 3; display: flex; align-items: center; gap: 12px; padding: 10px 12px; border: 1px solid #f59e0b; border-radius: 10px; background: #fffbeb; color: #92400e; font-size: 12px; box-shadow: 0 8px 20px rgba(120,53,15,.12); }
.nx-editor-conflict[hidden] { display: none; }
.nx-conflict-reload { padding: 6px 9px; border: 0; border-radius: 7px; background: #d97706; color: #fff; font-weight: 700; cursor: pointer; }
.nx-editor-tool { padding: 8px 13px; border: 1px solid #d6dce7; border-radius: 8px; background: #fff; color: #334155; font-weight: 700; cursor: pointer; }
.nx-editor-tool:hover { border-color: #6366f1; color: #4f46e5; }
.nx-editor-page { flex: 1; overflow: auto; padding: 38px 24px 70px; }
.nx-document-page { width: min(820px, 100%); min-height: 900px; margin: 0 auto; padding: 72px 80px; outline: none; background: #fff; box-shadow: 0 12px 34px rgba(15,23,42,.12); }
.nx-document-page h1 { margin-top: 0; font-size: 36px; }
.nx-document-page p { color: #526174; font-size: 17px; line-height: 1.7; }
.nx-demo-table { width: 100%; margin: 24px 0; border-collapse: collapse; }
.nx-demo-table td { padding: 10px; border: 1px solid #cbd5e1; }
.nx-slide-page { display: grid; place-items: center; }
.nx-slide-rail { position: absolute; left: 0; top: 128px; bottom: 0; z-index: 2; width: 86px; padding: 18px 12px; background: #f8fafc; border-right: 1px solid #dbe2ea; overflow-y: auto; }
.nx-slide-rail[hidden] { display: none; }
.nx-slide-thumb { display: grid; place-items: center; width: 58px; height: 38px; margin-bottom: 10px; border: 1px solid #cbd5e1; border-radius: 7px; background: #fff; color: #475569; cursor: pointer; }
.nx-slide-thumb.is-active, .nx-slide-thumb:hover { border-color: #6366f1; color: #4f46e5; box-shadow: 0 0 0 2px #e0e7ff; }
.nx-slide-canvas { width: min(1100px, 100%); aspect-ratio: 16 / 9; display: flex; flex-direction: column; justify-content: center; padding: 8%; outline: none; background: linear-gradient(135deg, #fff 0%, #eef2ff 100%); box-shadow: 0 20px 55px rgba(15,23,42,.18); }
.nx-slide-canvas.is-accent { background: linear-gradient(135deg, #151b36 0%, #4338ca 100%); color: #fff; }
.nx-slide-canvas h1 { max-width: 720px; margin: 12px 0; font-size: clamp(30px, 5vw, 68px); letter-spacing: -.04em; }
.nx-slide-canvas p { color: #64748b; font-size: 20px; }
.nx-slide-canvas.is-accent p { color: #c7d2fe; }
.nx-slide-kicker { color: #6366f1; font-size: 12px; font-weight: 800; letter-spacing: .2em; }
.nx-slide-canvas.is-accent .nx-slide-kicker { color: #c7d2fe; }
.nx-slide-shape { display: grid; place-items: center; width: 180px; height: 90px; margin-top: 20px; border-radius: 18px; background: #6366f1; color: #fff; font-weight: 800; box-shadow: 0 12px 24px rgba(79,70,229,.25); }
.nx-slide-image { display: block; max-width: 70%; max-height: 42%; margin-top: 20px; border-radius: 12px; object-fit: contain; box-shadow: 0 10px 24px rgba(15,23,42,.16); }
@media (max-width: 760px) { .rz-top-header { padding: 0 12px; gap: 10px; } .rz-header-workspace-select { min-width: 44px; width: 44px; padding: 0; justify-content: center; } .rz-header-workspace-select > span { font-size: 0; } .rz-header-workspace-select > span::after { content: 'NS'; font-size: 12px; } .rz-header-command-trigger { flex: 1; } .rz-main-workspace > .rz-contextual-bar { padding: 8px 12px; overflow-x: auto; } .nx-mode-dialog { padding: 26px 18px; } .nx-mode-cards { grid-template-columns: 1fr; } }
.rz-ctx-btn { border-radius: 10px; border-color: #e5e7eb; }
.rz-ctx-btn:hover { background: #f3efff; color: #5b21b6; }
.rz-ctx-btn.is-primary { background: #5b21b6; border-color: #5b21b6; }
.rz-main-workspace > .formula-bar { flex: 0 0 auto; }
.rz-main-workspace > .grid-view { flex: 1 1 auto; min-height: 240px; }
.rz-main-workspace > .tab-bar { flex: 0 0 auto; }
@media (max-width: 768px) {
  .rz-top-header { flex-basis: 54px; height: 54px; padding: 0 10px; }
  .rz-header-command-trigger { flex: 1; max-width: none; }
  .rz-main-workspace > .rz-contextual-bar { bottom: 54px; max-width: calc(100vw - 16px); overflow-x: auto; }
}
`;
